// pages/dashboard/sprintdashboard/page.js
import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getAllSprints, getAllStory, getAllUsers } from "../../api/adminDashboard";
import { getAllDepartments, getAllLeaveApplications } from "../../api/onboarddeboard";
import StatusCards from "./components/StatusCards/page";
import Charts from "./components/Charts/page";
import Blockers from "./components/Blockers/page";
import ScopeChanges from "./components/ScopeChanges/page";
import WorkloadDistribution from "./components/WorkloadDistribution/page";
import AssigneeBreakdown from "./components/AssigneeBreakdown/page";

/* ── Sunday-free day counting (same logic as Active Sprint) ── */
const dayNum = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return Date.UTC(y, m - 1, d) / 86400000;
};
const isSunday = (n) => (((n % 7) + 7) % 7) === 3;          // 3 = Sunday
const countNonSundays = (from, count) => {
  let c = 0;
  for (let i = 0; i < count; i++) if (!isSunday(from + i)) c++;
  return c;
};

// positive = days left, negative = overdue (Sundays not counted)
const sprintDaysLeft = (endDate) => {
  if (!endDate) return null;
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86400000;
  const end = dayNum(endDate);
  const diff = end - today;
  if (diff >= 0) return countNonSundays(today, diff);
  const n = countNonSundays(end + 1, -diff);
  return n ? -n : 0;
};

const sid = (v) => String(v ?? "");

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric"
  });
};

const SprintDashboard = () => {
  const [allSprints, setAllSprints] = useState([]);
  const [allStories, setAllStories] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    Promise.all([
      getAllSprints(token).then((r) => r.data).catch(() => []),
      getAllStory(token).then((r) => r.data).catch(() => []),
      getAllUsers(token).then((r) => r.data).catch(() => []),
      getAllDepartments(token).then((r) => r.data).catch(() => []),
      getAllLeaveApplications(token).then((r) => r.data).catch(() => []),
    ]).then(([sprints, stories, users, depts, leaves]) => {
      const spArr = Array.isArray(sprints) ? sprints : [];
      const stArr = Array.isArray(stories) ? stories : [];
      const usArr = Array.isArray(users) ? users : [];

      setAllSprints(spArr);
      setAllStories(stArr);
      setAllUsers(usArr);
      setAllDepartments(Array.isArray(depts) ? depts : []);
      setAllLeaves(Array.isArray(leaves) ? leaves : []);
      if (spArr.length) {
        const saved = sessionStorage.getItem("selectedSprintId");
        if (saved) {
          setSelId(saved);
        } else {
          // 1st priority: sprint explicitly marked ACTIVE in DB
          const activeSprint = spArr.find((sp) => sp.sprintStatus === "ACTIVE");

          // 2nd priority: most recently ended sprint (latest endDate in the past)
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const pastSprints = spArr.filter((sp) => {
            if (!sp.endDate) return false;
            const end = new Date(sp.endDate); end.setHours(0, 0, 0, 0);
            return end < today;
          });
          const recentSprint = pastSprints.sort(
            (a, b) => new Date(b.endDate) - new Date(a.endDate)
          )[0];

          // 3rd priority: last sprint in the list
          const fallback = spArr[spArr.length - 1];

          setSelId(sid((activeSprint || recentSprint || fallback).id));
        }
      }
      setLoading(false);
    });
  }, []);

  const sprint = useMemo(
    () => allSprints.find((s) => sid(s.id) === selId) || allSprints[allSprints.length - 1],
    [allSprints, selId]
  );

  const sprintStories = useMemo(() => {
    if (!sprint) return [];
    const spId = sid(sprint.id);
    return allStories.filter((st) => {
      if (st.sprint?.id !== undefined) return sid(st.sprint.id) === spId;
      if (st.sprintId !== undefined) return sid(st.sprintId) === spId;
      return false;
    });
  }, [sprint, allStories]);

  const sprintDays = sprint && sprint.startDate && sprint.endDate
    ? Math.max(0, countNonSundays(
      dayNum(sprint.startDate),
      dayNum(sprint.endDate) - dayNum(sprint.startDate) + 1
    ))
    : 0;

  const assigneeCount = useMemo(() => {
    const ids = new Set(
      sprintStories
        .map((st) => sid(st.assignee?.id || st.assigneeId || st.assignee))
        .filter((v) => v && v !== "undefined")
    );
    return ids.size;
  }, [sprintStories]);

  const completedCount = sprintStories.filter((s) => s.status === "COMPLETED").length;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const sprintEndDate = sprint?.endDate
    ? new Date(sprint.endDate)
    : null; if (sprintEndDate) sprintEndDate.setHours(0, 0, 0, 0);

  const isSprintCompleted = sprint?.sprintStatus === "COMPLETED"; // sprint is closed — stop counting overdue days

  const daysDiff = sprintEndDate ? sprintDaysLeft(sprint.endDate) : null; const isOverdue = !isSprintCompleted && daysDiff !== null && daysDiff < 0;

  const isOnTrack = !isOverdue && (sprintStories.length > 0
    ? completedCount / sprintStories.length >= 0.5
    : false);

  const daysMessage = isSprintCompleted
    ? "Ended"
    : (isOverdue
      ? `Overdue by: ${Math.abs(daysDiff)} Day${Math.abs(daysDiff) === 1 ? "" : "s"}`
      : (daysDiff !== null ? `Due in: ${daysDiff} Day${daysDiff === 1 ? "" : "s"}` : null));

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
      <Spinner animation="border" style={{ color: "#2563eb" }} />
      <span style={{ marginLeft: 12, color: "#6b7280", fontSize: 13 }}>Loading sprint data…</span>
    </div>
  );

  if (!sprint) return (
    <Container fluid style={{ padding: 24 }}>
      <p style={{ color: "#6b7280", fontSize: 13 }}>
        No sprint data found. Please create a sprint first from the Sprints menu.
      </p>
    </Container>
  );

  return (
    <Container fluid style={{ padding: "2px 2px 32px" }}>

      <div style={{ marginBottom: 14 }}>
        <h5 style={{ fontSize: 17, fontWeight: 700, color: "#111827", margin: 0 }}>
          Global Solution's Orbit
        </h5>
        <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
          Monitor sprint progress, team performance, and delivery health.
        </p>
      </div>

      {/* SPRINT BAR */}
      <div style={{
        background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10,
        padding: "10px 16px", marginBottom: 16,
      }}>
        {isMobile ? (
          /* MOBILE FIXED */
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

            {/* Row 1 */}
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{
                fontSize: 10, color: "#9ca3af", fontWeight: 700,
                textTransform: "uppercase", letterSpacing: ".6px",
              }}>
                Sprint:
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                {sprint.sprintName}
              </span>
            </div>

            {/* Row 2 */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#6b7280", fontSize: 11 }}>
                <CalendarTodayIcon sx={{ fontSize: 12 }} />
                {fmtDate(sprint.startDate)} → {fmtDate(sprint.endDate)}
              </div>

              {sprintDays > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#6b7280", fontSize: 11 }}>
                  <AccessTimeIcon sx={{ fontSize: 12 }} /> {sprintDays} Days
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#6b7280", fontSize: 11 }}>
                <MenuBookIcon sx={{ fontSize: 12 }} /> {sprintStories.length} Stories
              </div>
            </div>

            {/* Row 3 */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ position: "relative", flex: 1 }}>
                <select
                  value={selId || ""}
                  onChange={(e) => { setSelId(e.target.value); sessionStorage.setItem("selectedSprintId", e.target.value); }}
                  style={{
                    border: "1px solid #d1d5db", borderRadius: 8,
                    padding: "5px 24px 5px 10px",
                    fontSize: 12, fontWeight: 600,
                    appearance: "none", width: "100%",
                  }}
                >
                  {allSprints.map((s) => (
                    <option key={s.id} value={sid(s.id)}>
                      {s.sprintName}
                    </option>
                  ))}
                </select>

                <ExpandMoreIcon sx={{
                  fontSize: 14, position: "absolute", right: 6, top: "50%",
                  transform: "translateY(-50%)", pointerEvents: "none",
                }} />
              </div>

              <span style={{
                fontSize: 11, fontWeight: 700, padding: "5px 10px",
                borderRadius: 20,
                background: isOnTrack ? "#dcfce7" : "#fee2e2",
                color: isOnTrack ? "#16a34a" : "#dc2626",
                display: "flex", alignItems: "center", gap: 3,
              }}>
                {isOnTrack
                  ? <TrendingUpIcon sx={{ fontSize: 13 }} />
                  : <TrendingDownIcon sx={{ fontSize: 13 }} />}
                {isSprintCompleted ? "Completed" : (isOverdue ? "Delayed" : (isOnTrack ? "On Track" : "Behind"))}
              </span>
            </div>

          </div>
        ) : (
          /* DESKTOP ORIGINAL */
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between", flexWrap: "nowrap", gap: 10,
          }}>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{
                  fontSize: 10, color: "#9ca3af", fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: ".6px",
                }}>
                  Sprint:
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                  {sprint.sprintName}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#6b7280", fontSize: 12 }}>
                <CalendarTodayIcon sx={{ fontSize: 13 }} />
                {fmtDate(sprint.startDate)} → {fmtDate(sprint.endDate)}
              </div>

              {sprintDays > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#6b7280", fontSize: 12 }}>
                  <AccessTimeIcon sx={{ fontSize: 13 }} /> {sprintDays} Days
                </div>
              )}

              {assigneeCount > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#6b7280", fontSize: 12 }}>
                  <PeopleOutlineIcon sx={{ fontSize: 13 }} /> {assigneeCount} Assignees
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#6b7280", fontSize: 12 }}>
                <MenuBookIcon sx={{ fontSize: 13 }} /> {sprintStories.length} Stories
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ position: "relative" }}>
                <select
                  value={selId || ""}
                  onChange={(e) => { setSelId(e.target.value); sessionStorage.setItem("selectedSprintId", e.target.value); }} style={{
                    border: "1px solid #d1d5db", borderRadius: 8,
                    padding: "5px 28px 5px 10px",
                    fontSize: 12, fontWeight: 600,
                    appearance: "none",
                  }}
                >
                  {allSprints.map((s) => (
                    <option key={s.id} value={sid(s.id)}>
                      {s.sprintName}
                    </option>
                  ))}
                </select>

                <ExpandMoreIcon sx={{
                  fontSize: 16, position: "absolute", right: 6, top: "50%",
                  transform: "translateY(-50%)", pointerEvents: "none",
                }} />
              </div>

              <span style={{
                fontSize: 11, fontWeight: 700, padding: "4px 10px",
                borderRadius: 20,
                background: isOnTrack ? "#dcfce7" : "#fee2e2",
                color: isOnTrack ? "#16a34a" : "#dc2626",
                display: "flex", alignItems: "center", gap: 3,
              }}>
                {isOnTrack
                  ? <TrendingUpIcon sx={{ fontSize: 14 }} />
                  : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                {isOverdue ? "Delayed" : (isOnTrack ? "On Track" : "Behind")}
              </span>
            </div>

          </div>
        )}

        {daysMessage && (
          <div style={{
            marginTop: 8, fontSize: 11, fontWeight: 700,
            color: isOverdue ? "#dc2626" : "#6b7280",
          }}>
            {daysMessage}
          </div>
        )}
      </div>

      <StatusCards stories={sprintStories} />
      <Charts sprint={sprint} stories={sprintStories} allSprints={allSprints} allStories={allStories} />

      <Row className="g-3 mb-3">
        <Col md={4}><Blockers stories={sprintStories} /></Col>
        <Col md={4}><ScopeChanges sprint={sprint} stories={sprintStories} /></Col>
        <Col md={4}><WorkloadDistribution stories={sprintStories} users={allUsers} sprint={sprint} departments={allDepartments} leaves={allLeaves} /></Col>      </Row>

      <AssigneeBreakdown stories={sprintStories} users={allUsers} />

    </Container>
  );
};

export default SprintDashboard;
